import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { theme, useMediaQuery, useThemeStore } from 'academy-common-lib/utils/Styling';
import { getProxyUrl, getPublicLang } from 'academy-common-lib/utils/url';
import { H3 } from 'foundations-components/transitional/components/H3';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Box } from 'foundations-components/transitional/components/Box';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { Link } from 'foundations-components/transitional/components/Link';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Image } from 'foundations-components/transitional/components/Image';

// @ts-expect-error Could not find a declaration file for module
import coursesCardThumbnail from 'bender-url!../../../../images/courses-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import certificationsCardThumbnail from 'bender-url!../../../../images/certifications-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import learningPathsCardThumbnail from 'bender-url!../../../../images/learningPaths-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import bootcampsCardThumbnail from 'bender-url!../../../../images/bootcamps-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import accreditationsCardThumbnail from 'bender-url!../../../../images/accreditations-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import guidedOnboardingCardThumbnail from 'bender-url!../../../../images/guidedOnboarding-card-thumbnail.webp';
// @ts-expect-error Could not find a declaration file for module
import hubspotUserGroupsCardThumbnail from 'bender-url!../../../../images/hubspotUserGroups-card-thumbnail.webp';
import { publicNavButtonHoverStyle, publicNavButtonStyle } from '../publicNavButtonStyle';
import usageTracker from '../../../utils/usageTracker';
import { H4 } from 'foundations-components/transitional/components/H4';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NavButton = styled.button.withConfig({
  displayName: "PublicPageNavDropdown__NavButton"
})(["", " &[aria-expanded='true']{", "}"], publicNavButtonStyle, publicNavButtonHoverStyle);
const StyledLink = styled.div.withConfig({
  displayName: "PublicPageNavDropdown__StyledLink"
})(({
  theme: {
    color,
    space
  }
}) => {
  return {
    img: {
      borderRadius: space['100']
    },
    '&:hover': {
      color: color['text-core-default'],
      textDecoration: 'none',
      boxShadow: '0 8px 16px rgba(33, 51, 67, 0.08)'
    }
  };
});
const DropdownCardLink = ({
  thumbnailSrc,
  title,
  description,
  href
}) => {
  const handleNavItemClick = e => {
    usageTracker.track('navInteraction', {
      action: 'nav-dropdown-item-click',
      href: e.currentTarget.href
    });
  };
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledLink, {
      children: /*#__PURE__*/_jsx(Link, {
        href: href,
        onClick: handleNavItemClick,
        target: "_blank",
        use: "unstyled",
        children: /*#__PURE__*/_jsx(View, {
          backgroundColor: "do-not-use-olaf",
          blockSize: "100%",
          borderRadius: "200",
          boxShadow: "100",
          inlineSize: "100%",
          padding: "400",
          children: /*#__PURE__*/_jsxs(TFlex, {
            alignItems: "start",
            color: "do-not-use-obsidian",
            flexDirection: "row",
            margin: "0",
            padding: "0",
            children: [/*#__PURE__*/_jsx(Image, {
              alt: "card thumbnail",
              height: 32,
              loading: "lazy",
              marginInlineEnd: "300",
              src: thumbnailSrc,
              width: 32
            }), /*#__PURE__*/_jsxs(TFlex, {
              flexDirection: "column",
              gap: 16,
              children: [/*#__PURE__*/_jsx(H4, {
                margin: "0",
                padding: "0",
                children: title
              }), /*#__PURE__*/_jsx(Paragraph, {
                margin: "0",
                padding: "0",
                typography: "body-100",
                children: description
              })]
            })]
          })
        })
      })
    })
  );
};
const StyledTextColor = styled.div.withConfig({
  displayName: "PublicPageNavDropdown__StyledTextColor"
})(({
  theme: {
    color
  },
  expanded
}) => {
  const {
    theme: activeTheme
  } = useThemeStore();
  return {
    color: expanded ? activeTheme === 'highContrast' ? theme.colors.HIGH_CONTRAST_LINK : color['text-interactive-default'] : undefined
  };
});
const StyledDownIcon = styled.div.withConfig({
  displayName: "PublicPageNavDropdown__StyledDownIcon"
})(({
  expanded
}) => {
  const {
    isMobile
  } = useMediaQuery();
  return {
    svg: Object.assign({
      transition: 'transform 200ms ease-in-out'
    }, expanded ? Object.assign({
      transform: 'rotate(180deg)'
    }, isMobile && {
      transform: 'rotate(90deg)',
      position: 'absolute',
      right: '12px'
    }) : Object.assign({
      transform: 'translateY(1px)'
    }, isMobile && {
      transform: 'rotate(-90deg)',
      position: 'absolute'
    }))
  };
});
const PublicPageNavDropdown = () => {
  const {
    isMobile
  } = useMediaQuery();
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    usageTracker.track('navInteraction', {
      action: expanded ? 'nav-dropdown-open' : 'nav-dropdown-close'
    });
  }, [expanded]);
  return /*#__PURE__*/_jsxs("div", {
    onMouseLeave: () => setExpanded(false),
    style: {
      height: '100%'
    },
    children: [/*#__PURE__*/_jsx(NavButton, {
      "aria-controls": "learningAcademy",
      "aria-expanded": expanded,
      onClick: () => setExpanded(true),
      onMouseEnter: () => setExpanded(true),
      children: /*#__PURE__*/_jsxs(TFlex, {
        alignItems: "center",
        direction: "row",
        justifyContent: isMobile ? 'space-between' : 'start',
        margin: "0",
        padding: "0",
        children: [/*#__PURE__*/_jsx(Paragraph, {
          margin: "0",
          marginInlineEnd: "200",
          padding: "0",
          textDecoration: expanded ? 'underline' : 'none',
          children: /*#__PURE__*/_jsx(StyledTextColor, {
            expanded: expanded,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademy"
            })
          })
        }), /*#__PURE__*/_jsx(StyledDownIcon, {
          expanded: expanded,
          children: /*#__PURE__*/_jsx(IconHolder, {
            color: "do-not-use-eerie",
            icon: DownIcon,
            padding: "0",
            size: "200"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(View, {
      "aria-hidden": !expanded,
      backgroundColor: "do-not-use-gypsum",
      blockSize: isMobile ? '100%' : undefined,
      borderColor: "border-core-subtle",
      borderStyle: "solid",
      borderWidth: "100",
      boxShadow: "300",
      "data-test-id": "public-nav-dropdown-content",
      display: expanded ? 'block' : 'none',
      id: "learningAcademy",
      inlineSize: "100%",
      insetBlockStart: isMobile ? '0' : undefined,
      insetInlineStart: "0",
      overflow: isMobile ? 'scroll' : 'visible',
      paddingBlock: isMobile ? '400' : '600',
      paddingInline: isMobile ? '400' : '1500',
      position: "absolute",
      zIndex: "150",
      children: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        direction: "column",
        gap: 16,
        justify: "center",
        children: [/*#__PURE__*/_jsxs(Box, {
          marginBlockEnd: isMobile ? '0' : '600',
          marginBlockStart: isMobile ? '0' : '400',
          children: [/*#__PURE__*/_jsx(H3, {
            textAlign: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.title"
            })
          }), /*#__PURE__*/_jsx(Paragraph, {
            margin: "0",
            maxInlineSize: "462px",
            padding: "0",
            textAlign: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.body"
            })
          })]
        }), /*#__PURE__*/_jsxs(CardGrid, {
          gap: isMobile ? '400' : '600',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.courses.body"
            }),
            href: getProxyUrl('courses'),
            thumbnailSrc: coursesCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.courses.title"
            })
          }), /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.certifications.body"
            }),
            href: getProxyUrl('certification-overview'),
            thumbnailSrc: certificationsCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.certifications.title"
            })
          }), /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.bootcamps.body"
            }),
            href: "https://www.hubspot.com/academy/bootcamps/home",
            thumbnailSrc: bootcampsCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.bootcamps.title"
            })
          }), /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.learningPaths.body"
            }),
            href: getProxyUrl('learning-paths'),
            thumbnailSrc: learningPathsCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.learningPaths.title"
            })
          }), /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.hubspotUserGroups.body"
            }),
            href: "https://www.hubspot.com/hubspot-user-groups",
            thumbnailSrc: hubspotUserGroupsCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.hubspotUserGroups.title"
            })
          }), /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.accreditations.body"
            }),
            href: "https://www.hubspot.com/partnercredentials",
            thumbnailSrc: accreditationsCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.accreditations.title"
            })
          }), getPublicLang() === 'en' && /*#__PURE__*/_jsx(DropdownCardLink, {
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.guidedOnboarding.body"
            }),
            href: "https://www.hubspot.com/academy/guided-onboarding",
            thumbnailSrc: guidedOnboardingCardThumbnail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyPublic.publicNav.exploreAcademyContent.links.guidedOnboarding.title"
            })
          })]
        })]
      })
    }), expanded && /*#__PURE__*/_jsx(View, {
      backgroundColor: "do-not-use-olaf",
      blockSize: "59px",
      borderStyle: "none",
      inlineSize: "80vw",
      insetBlockStart: `-${theme.sizes.PUBLIC_NAV_REDUCED_HEIGHT}`,
      insetInlineStart: "0",
      onClick: () => setExpanded(false),
      padding: "200",
      position: "absolute",
      textAlign: "left",
      zIndex: "150",
      children: /*#__PURE__*/_jsxs(Paragraph, {
        alignItems: "center",
        blockSize: "100%",
        color: "do-not-use-heffalump",
        display: "flex",
        gap: "100",
        margin: "0",
        padding: "0",
        children: [/*#__PURE__*/_jsx(LeftIcon, {
          size: "200"
        }), /*#__PURE__*/_jsx(Paragraph, {
          margin: "0",
          padding: "0",
          textTransform: "uppercase",
          typography: "heading-100",
          children: "Home"
        })]
      })
    })]
  });
};
export default PublicPageNavDropdown;